/**
 * features/burs/ITW7Return.tsx
 * ------------------------------------------------------------------
 * BURS — ITW-7 monthly PAYE return. Pick a month; aggregates that
 * month's generated payslips into the PAYE due to BURS, with a
 * per-employee preview, PDF, and CSV. Source = payslips, so run the
 * Pay Run for the month first. Renders embedded in the BURS hub.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { Download, FileText, AlertTriangle } from 'lucide-react';
import { SoftCard, StatChip, glassFieldClass } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useHrData } from '@/features/hr/useHrData';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { fmtPula, MONTH_LABELS } from '@/lib/hrPayroll';
import {
  buildMonthlyReturn,
  resolveEmployerDetails,
  toCsv,
  downloadCsv,
} from '@/lib/bursForms';
import { buildItw7Pdf } from '@/lib/bursPdf';

export default function ITW7Return() {
  const data = useHrData();
  const { userData } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [logo, setLogo] = useState<string | null>(null);

  useEffect(() => { loadLogoDataUrl().then(setLogo); }, []);

  const ret = useMemo(() => buildMonthlyReturn(data.payslips, year, month), [data.payslips, year, month]);
  const emp = resolveEmployerDetails(data.payrollSettings);

  function downloadPdf() {
    const pdf = buildItw7Pdf(ret, emp, { logoDataUrl: logo, preparedBy: userData?.displayName });
    pdf.save(`ITW-7_${year}-${String(month).padStart(2, '0')}.pdf`);
  }

  function exportCsv() {
    const head = ['Employee No', 'Name', 'Gross Emoluments', 'PAYE', 'Lump-sum Tax', 'Total Tax'];
    const body = ret.rows.map((r) => [r.employeeNo, r.name, r.gross.toFixed(2), r.paye.toFixed(2), r.gratuityTax.toFixed(2), r.totalTax.toFixed(2)]);
    const foot = ['', `TOTAL (${ret.totals.employees} employees)`, ret.totals.gross.toFixed(2), ret.totals.paye.toFixed(2), ret.totals.gratuityTax.toFixed(2), ret.totals.totalTax.toFixed(2)];
    downloadCsv(`ITW-7_${year}-${String(month).padStart(2, '0')}.csv`, toCsv([head, ...body, foot]));
  }

  if (data.loading) {
    return <div className="flex justify-center py-16"><Spinner label="Loading payroll…" /></div>;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-500 dark:text-gray-400">{ret.monthLabel} · PAYE due to BURS · from generated payslips</p>
        <div className="flex items-center gap-2">
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={glassFieldClass('py-1.5 text-sm')}>
            {MONTH_LABELS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className={glassFieldClass('py-1.5 text-sm w-24')} />
        </div>
      </div>

      {ret.rows.length === 0 ? (
        <SoftCard hover={false}>
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 text-sm">
            <AlertTriangle size={16} />
            No payslips generated for {ret.monthLabel}. Run the Pay Run for this month, then the return will populate here.
          </div>
        </SoftCard>
      ) : (
        <SoftCard pad="p-0" hover={false}>
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <StatChip tone="emerald" value={`P${fmtPula(ret.totals.totalTax)}`} label="PAYE due" />
              <StatChip tone="slate" value={String(ret.totals.employees)} label="employees" />
            </div>
            <div className="flex items-center gap-2">
              <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl">
                <Download size={15} /> CSV
              </button>
              <button onClick={downloadPdf} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-[var(--accent-strong,#1b3a6b)] hover:bg-[var(--accent-strong-hover,#162e52)] rounded-xl">
                <FileText size={15} /> Download ITW-7 PDF
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-2 px-4 font-medium">Employee No</th>
                  <th className="py-2 px-4 font-medium">Name</th>
                  <th className="py-2 px-4 font-medium text-right">Gross</th>
                  <th className="py-2 px-4 font-medium text-right">PAYE</th>
                  {ret.hasGratuityTax && <th className="py-2 px-4 font-medium text-right">Lump-sum tax</th>}
                  <th className="py-2 px-4 font-medium text-right">Total tax</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {ret.rows.map((r) => (
                  <tr key={r.staffId}>
                    <td className="py-2 px-4 text-gray-500 dark:text-gray-400">#{r.employeeNo}</td>
                    <td className="py-2 px-4 text-gray-800 dark:text-gray-100">{r.name}</td>
                    <td className="py-2 px-4 text-right text-gray-800 dark:text-gray-100">{fmtPula(r.gross)}</td>
                    <td className="py-2 px-4 text-right text-gray-800 dark:text-gray-100">{fmtPula(r.paye)}</td>
                    {ret.hasGratuityTax && <td className="py-2 px-4 text-right text-gray-500 dark:text-gray-400">{fmtPula(r.gratuityTax)}</td>}
                    <td className="py-2 px-4 text-right font-medium text-gray-900 dark:text-white">{fmtPula(r.totalTax)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 dark:border-gray-600 font-semibold text-gray-800 dark:text-gray-100">
                  <td className="py-2.5 px-4" colSpan={2}>Total · {ret.totals.employees} employees</td>
                  <td className="py-2.5 px-4 text-right">{fmtPula(ret.totals.gross)}</td>
                  <td className="py-2.5 px-4 text-right">{fmtPula(ret.totals.paye)}</td>
                  {ret.hasGratuityTax && <td className="py-2.5 px-4 text-right">{fmtPula(ret.totals.gratuityTax)}</td>}
                  <td className="py-2.5 px-4 text-right">{fmtPula(ret.totals.totalTax)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </SoftCard>
      )}
    </div>
  );
}
